import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { SnackbarProvider } from 'notistack';
import { LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/en-au';  // Import Australian locale
import { DateProvider } from './context/DateContext';  // Import DateProvider
import ErrorBoundary from './components/ErrorBoundary';
import DashboardLayout from './layouts/DashboardLayout';
import Dashboard from './pages/Dashboard';
import Clients from './pages/Clients';
import ClientDetails from './pages/ClientDetails';
import Locations from './pages/Locations';
import LocationDetails from './pages/LocationDetails';
import Insights from './pages/Insights';
import PurePromote from './pages/PurePromote';
import Marketing from './pages/Marketing';
import Outreach from './pages/Outreach';
import Registrations from './pages/Registrations';
import ClientWatchlist from './pages/ClientWatchlist';
import Onboarding from './pages/Onboarding';
import CeoReportingDashboard from './pages/CeoReportingDashboard';
import CeoMetricDetailPage from './pages/CeoMetricDetailPage';
import { isAdminNav, isSuperadmin } from './utils/roles';
import './index.css';

// Set dayjs to use Australian locale
dayjs.locale('en-au');

function App({ onLogout, userRole }) {
  return (
    <ErrorBoundary>
      <DateProvider>
        <LocalizationProvider 
          dateAdapter={AdapterDayjs} 
          adapterLocale="en-au"
          localeText={{ 
            start: 'Start Date', 
            end: 'End Date',
            fieldMonthPlaceholder: () => 'MM',
            fieldDayPlaceholder: () => 'DD',
            fieldYearPlaceholder: () => 'YYYY',
          }}
        >
          <SnackbarProvider maxSnack={3} autoHideDuration={3000}>
            <Router>
              <ErrorBoundary>
                <Routes>
                  <Route path="/" element={<DashboardLayout onLogout={onLogout} userRole={userRole} />}>
                    {isAdminNav(userRole) ? (
                      <>
                        {isSuperadmin(userRole) ? (
                          <>
                            <Route index element={<Navigate to="/ceo" replace />} />
                            <Route path="ceo" element={<CeoReportingDashboard />} />
                            <Route path="ceo/detail/:metric" element={<CeoMetricDetailPage />} />
                            <Route path="dashboard" element={<Dashboard />} />
                          </>
                        ) : (
                          <>
                            <Route index element={<Dashboard />} />
                            <Route path="ceo" element={<Navigate to="/" replace />} />
                          </>
                        )}
                        <Route path="clients" element={<Clients />} />
                        <Route path="clients/:clientId" element={<ClientDetails />} />
                        <Route path="locations" element={<Locations />} />
                        <Route path="locations/:locationId" element={<LocationDetails />} />
                        <Route path="insights" element={<Insights />} />
                        <Route path="pure-promote" element={<PurePromote />} />
                        <Route path="onboarding" element={<Onboarding />} />
                        <Route path="outreach" element={<Outreach />} />
                        <Route path="marketing" element={<Marketing />} />
                        <Route path="registrations" element={<Registrations />} />
                        <Route path="client-watchlist" element={<ClientWatchlist />} />
                      </>
                    ) : (
                      <>
                        <Route path="clients" element={<Clients />} />
                        <Route path="clients/:clientId" element={<ClientDetails />} />
                        <Route path="locations" element={<Locations />} />
                        <Route path="locations/:locationId" element={<LocationDetails />} />
                        <Route path="pure-promote" element={<PurePromote />} />
                        <Route path="onboarding" element={<Onboarding />} />
                        <Route path="outreach" element={<Outreach />} />
                        <Route path="registrations" element={<Registrations />} />
                        <Route path="*" element={<Clients />} />
                      </>
                    )}
                  </Route>
                </Routes>
              </ErrorBoundary>
            </Router>
          </SnackbarProvider>
        </LocalizationProvider>
      </DateProvider>
    </ErrorBoundary>
  );
}

export default App;
